<script lang="ts" setup>
import { onMounted } from "vue";
import { storeToRefs } from "pinia";

import notify from "@/libs/notify.ts";

import { create, update } from "@/repositories/transactions.ts";

import { fetchAll as fetchLedgers } from "@/repositories/ledgers.ts";
import { fetch as fetchTransaction, remove as removeTransaction } from "@/repositories/transactions.ts";

import { useAccount } from "@/stores/account.ts";
import { useDateRange } from "@/components/DateRange/state.ts";
import { useTransactions } from "@/stores/transactions.ts";
import { useEditTransaction } from "@/components/EditTransaction/state.ts";
import { useSidePanelState } from "@/components/SidePanel/state.ts";

import { LedgerFactory, type LedgerResource } from "@/factories/LedgerFactory.ts";
import { TransactionFactory, type TransactionResource } from "@/factories/TransactionFactory.ts";

import Notification, { Status } from "@/classes/Notification.ts";

import SelectLedger from "@/components/EditTransaction/SelectLedger.vue";
import EditName from "@/components/EditTransaction/EditName.vue";
import SelectDate from "@/components/EditTransaction/SelectDate.vue";
import EditAmount from "@/components/EditTransaction/EditAmount.vue";
import EditDescription from "@/components/EditTransaction/EditDescription.vue";
import SelectRecurrence from "@/components/EditTransaction/SelectRecurrence.vue";
import FormButtons from "@/components/EditTransaction/FormButtons.vue";

interface Props {
  id?: number;
  type: "expense" | "income";
}

const account = useAccount();
const transactions = useTransactions();
const sidePanelState = useSidePanelState();
const dateRange = useDateRange();
const state = useEditTransaction();

const { range } = storeToRefs(dateRange);

const props = defineProps<Props>();

const onCancel = (): void => {
  state.$reset();
  sidePanelState.close();
};

const onRemove = async (): Promise<void> => {
  const tx = state.toTransaction(props.id);

  const isRemoved = await removeTransaction(tx);

  if (isRemoved) {
    transactions.forget(tx);
    state.$reset();
    sidePanelState.close();
  }
};

const onSave = async () => {
  const tx = state.toTransaction(props.id);

  if (props.id !== undefined) {
    update(tx, range.value).then((res: TransactionResource | TransactionResource[]) => {
      notify(new Notification({ title: "Item opgeslagen", status: Status.SUCCESS, ttl: 2000 }));

      if (Array.isArray(res)) {
        transactions.forget(tx);
        res.forEach((txResource: TransactionResource) =>
          transactions.insert(new TransactionFactory().fromJson(txResource)),
        );
      } else {
        transactions.update(new TransactionFactory().fromJson(res));
      }
    });
  } else {
    create(tx, props.type, range.value).then((res: TransactionResource | TransactionResource[]) => {
      notify(new Notification({ title: "Item opgeslagen", status: Status.SUCCESS, ttl: 2000 }));

      if (Array.isArray(res)) {
        res.forEach((txResource: TransactionResource) => {
          transactions.insert(new TransactionFactory().fromJson(txResource));
        });
      } else {
        transactions.insert(new TransactionFactory().fromJson(res));
      }
    });
  }

  state.$reset();
  sidePanelState.close();
};

onMounted(async () => {
  const ledgers = await fetchLedgers();

  account.setLedgers(
    ledgers.map((resource: LedgerResource) => {
      return new LedgerFactory().fromJson(resource);
    }),
  );

  if (props.id === undefined) {
    state.type = props.type;
    // A new transaction still needs a ledger, or saving dereferences `ledger.id` on undefined.
    state.ledger = account.defaultLedger;

    return;
  }

  const transaction = new TransactionFactory().fromJson(await fetchTransaction(props.id));

  state.hydrate({
    amount: transaction.amount,
    applyToIncomeTax: transaction.applyToIncomeTax,
    currency: transaction.currency ?? "EUR",
    date: transaction.date,
    description: transaction.description,
    group: transaction.group,
    isDisabled: transaction.isDisabled,
    ledger: transaction.ledger,
    name: transaction.name,
    recurrence: transaction.recurrence,
    settlesReservation: transaction.settlesReservation,
  });

  state.type = transaction.type;
});
</script>

<template>
  <form>
    <div class="flex flex-col space-y-2">
      <SelectLedger></SelectLedger>
      <EditName></EditName>
      <SelectDate></SelectDate>
      <EditAmount></EditAmount>
      <EditDescription></EditDescription>
      <SelectRecurrence></SelectRecurrence>
      <FormButtons
        @cancel="onCancel"
        @remove="onRemove"
        @save="onSave"
      ></FormButtons>
    </div>
  </form>
</template>
